/**
 * ReadingProgress widget -- Articles read this week and continue reading.
 */

import type { Widget, WidgetContext } from '../types';

interface ReadingData {
  articlesThisWeek: number;
  readingStreak: number;
  continueArticle: { title: string; slug: string; progress: number } | null;
}

function getMockData(): ReadingData {
  try {
    const raw = localStorage.getItem('aweni_reading_progress');
    if (raw) return JSON.parse(raw);
  } catch { /* */ }
  return {
    articlesThisWeek: 3,
    readingStreak: 5,
    continueArticle: { title: '10 Study Techniques That Work', slug: 'learning-tips', progress: 45 },
  };
}

export const ReadingProgress: Widget = {
  id: 'reading-progress',
  label: { en: 'Reading', fr: 'Lecture', ar: 'القراءة', es: 'Lectura', de: 'Lesen' },
  icon: '📖',
  defaultVisible: true,
  defaultOrder: 16,
  defaultSize: 'compact',

  render(container: HTMLElement, size: 'compact' | 'expanded', ctx: WidgetContext): void {
    const data = getMockData();

    if (size === 'compact') {
      const continueText = data.continueArticle
        ? `${ctx.t('widget_continue_reading')}: ${data.continueArticle.title}`
        : `${data.articlesThisWeek} ${ctx.t('widget_articles_this_week')}`;

      container.innerHTML = `
        <div class="rounded-2xl p-4 cursor-pointer flex items-center justify-between" style="background:var(--card-bg);box-shadow:var(--shadow-sm);" data-nav="blog">
          <div class="flex items-center gap-2">
            <span>📖</span>
            <span class="text-sm truncate" style="color:var(--text-primary);max-width:200px;">${continueText}</span>
          </div>
          <span class="text-xs" style="color:var(--accent);">🔥 ${data.readingStreak}d</span>
        </div>`;
    } else {
      container.innerHTML = `
        <div class="rounded-2xl p-5" style="background:var(--card-bg);box-shadow:var(--shadow-sm);">
          <h3 class="text-sm font-semibold uppercase tracking-wide mb-3" style="color:var(--text-secondary);">📖 ${ctx.t('widget_reading_title')}</h3>

          <div class="flex items-center gap-4 mb-3">
            <div class="text-center">
              <p class="text-lg font-bold" style="color:var(--text-primary);">${data.articlesThisWeek}</p>
              <p class="text-xs" style="color:var(--text-secondary);">${ctx.t('widget_articles_this_week')}</p>
            </div>
            <div class="text-center">
              <p class="text-lg font-bold" style="color:#F59E0B;">🔥 ${data.readingStreak}</p>
              <p class="text-xs" style="color:var(--text-secondary);">${ctx.t('widget_reading_streak')}</p>
            </div>
          </div>

          ${data.continueArticle ? `
            <button class="w-full p-3 rounded-xl text-start transition-transform active:scale-95" style="background:var(--bg-secondary);" data-nav="blog">
              <p class="text-xs" style="color:var(--text-secondary);">${ctx.t('widget_continue_reading')}</p>
              <p class="text-sm font-medium truncate" style="color:var(--text-primary);">${data.continueArticle.title}</p>
              <div class="mt-2 rounded-full overflow-hidden" style="height:4px;background:var(--border);">
                <div class="rounded-full" style="height:100%;width:${data.continueArticle.progress}%;background:#14B8A6;"></div>
              </div>
            </button>
          ` : ''}
        </div>`;
    }

    container.querySelectorAll<HTMLElement>('[data-nav]').forEach((el) => {
      el.addEventListener('click', () => ctx.navigate('blog'));
    });
  },
};
